// CameraRig — maps the sim-space follow camera (GameCamera's smoothed
// center, unchanged math) onto a real PerspectiveCamera pose, and owns the
// overview framing used by menus/celebrations. Mode switches blend over a
// short lerp. Zero-alloc: scratch vectors only.

import type { Vec2 } from "@arcade-hockey/sim";
import { type PerspectiveCamera, Vector3 } from "three/webgpu";
import { type CameraStyle, cameraStyle } from "./style/style-schema.js";
import { simToWorldInto } from "./world-mapping.js";

export type RigMode = "follow" | "overview";

const TARGET_SCRATCH = new Vector3();
const POS_SCRATCH = new Vector3();
const BLEND_SECONDS = 0.6;

export class CameraRig {
  private mode: RigMode = "overview";
  private blend = 1; // 1 = fully at `mode`, <1 = blending from the other pose
  private readonly fromPos = new Vector3();
  private readonly fromTarget = new Vector3();
  private readonly pos = new Vector3();
  private readonly target = new Vector3();
  /** FX: additive FOV punch (degrees), decayed by FxDirector. */
  fovPunch = 0;

  constructor(private readonly camera: PerspectiveCamera) {}

  setMode(mode: RigMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.fromPos.copy(this.pos);
    this.fromTarget.copy(this.target);
    this.blend = 0;
  }

  currentMode(): RigMode {
    return this.mode;
  }

  /**
   * Per-frame pose update. `followCenter` is GameCamera's smoothed center
   * in sim space (ignored in overview mode).
   */
  update(followCenter: Vec2, dt: number): void {
    const style = cameraStyle;
    if (this.mode === "follow") {
      followPose(POS_SCRATCH, TARGET_SCRATCH, followCenter, style);
    } else {
      const ov = style.overview;
      POS_SCRATCH.set(ov.position.x, ov.position.y, ov.position.z);
      TARGET_SCRATCH.set(ov.lookAt.x, ov.lookAt.y, ov.lookAt.z);
    }

    if (this.blend < 1) {
      this.blend = Math.min(1, this.blend + dt / BLEND_SECONDS);
      const t = smootherstep(this.blend);
      this.pos.lerpVectors(this.fromPos, POS_SCRATCH, t);
      this.target.lerpVectors(this.fromTarget, TARGET_SCRATCH, t);
    } else {
      this.pos.copy(POS_SCRATCH);
      this.target.copy(TARGET_SCRATCH);
    }

    const fov =
      (this.mode === "follow" ? style.follow.fovDeg : style.overview.fovDeg) + this.fovPunch;
    if (fov !== this.camera.fov) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
    this.camera.position.copy(this.pos);
    this.camera.lookAt(this.target);
  }
}

function followPose(outPos: Vector3, outTarget: Vector3, center: Vec2, style: CameraStyle): void {
  simToWorldInto(outTarget, center);
  const pitch = (style.follow.pitchDeg * Math.PI) / 180;
  const d = style.follow.distance;
  // Camera sits toward the home end (+z) and above, looking down-rink.
  outPos.set(outTarget.x, outTarget.y + Math.sin(pitch) * d, outTarget.z + Math.cos(pitch) * d);
}

function smootherstep(t: number): number {
  return t * t * (3 - 2 * t);
}
